import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Job, PickupJob } from '@app/models/job.model';
import { User } from '@app/models/user.model';
import { AuthService } from '@app/services/auth/auth.service';
import { ClosedownService } from '@app/services/closedown/closedown.service';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { AllocationMode, JobStatus, JobType, OpenJobsListType } from '@app/shared/enums';

@Component({
  selector: 'app-open-jobs-list',
  templateUrl: './open-jobs-list.component.html',
  styleUrls: ['./open-jobs-list.component.scss'],
  standalone: false
})
export class OpenJobsListComponent implements OnInit {
  readonly AllocationMode = AllocationMode;
  jobs: Job[] = [];
  siteId = '';
  siteName = '';
  jobType = '';
  selectedSubSite = '';
  allocationType = '';
  hasLoaded = false;
  hasLoadError = false;
  currentUser?: User;
  selectedJob?: Job;
  allocationMode = AllocationMode.INTERNAL;
  allocation = {
    email: '',
    companyName: '',
    technicianName: '',
    phoneNumber: ''
  };
  isAllocating = false;
  allocationErrorKey = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly jobService: JobService,
    private readonly closedownService: ClosedownService,
    private readonly navigationService: NavigationService
  ) {
    this.navigationService.onChange(true, false);
  }

  get isPpm(): boolean {
    return this.jobType.toUpperCase() === JobType.PPM_JOB;
  }

  get jobTypeKey(): string {
    const keys: Record<string, string> = {
      [OpenJobsListType.NEW]: 'open_jobs_summary_new',
      [OpenJobsListType.ON_SITE]: 'open_jobs_summary_on_site',
      [OpenJobsListType.RETURNING]: 'open_jobs_summary_returning',
      [OpenJobsListType.AWAITING_PARTS]: 'open_jobs_summary_awaiting_parts',
      [JobType.PPM_JOB]: 'open_jobs_summary_ppm'
    };

    return keys[this.jobType.toUpperCase()] || 'open_jobs_summary_title';
  }

  get subSites(): string[] {
    return [...new Set(this.jobs.map(job => job.subSite).filter(Boolean))].sort();
  }

  get filteredJobs(): Job[] {
    return this.jobs.filter(job =>
      this.matchesJobType(job) &&
      (!this.selectedSubSite || job.subSite === this.selectedSubSite) &&
      (!this.allocationType ||
        (this.allocationType === 'allocated' ? Boolean(job.allocatedUserEmail) : !job.allocatedUserEmail))
    );
  }

  get canConfirmAllocation(): boolean {
    const hasCompany = this.allocationMode === AllocationMode.INTERNAL || Boolean(this.allocation.companyName.trim());
    return hasCompany && Boolean(
      this.allocation.email.trim() &&
      this.allocation.technicianName.trim() &&
      this.allocation.phoneNumber.trim()
    );
  }

  ngOnInit(): void {
    void this.authService.getUser().then(user => this.currentUser = user);
    this.siteId = this.route.snapshot.paramMap.get('siteId') || '';
    this.jobType = this.route.snapshot.paramMap.get('jobType') || '';
    this.siteName = this.route.snapshot.queryParamMap.get('siteName') || '';

    if (!this.siteId || !this.jobType) {
      this.hasLoadError = true;
      this.hasLoaded = true;
      return;
    }

    this.jobService.getResourceAdminJobsBySiteId(this.siteId).subscribe({
      next: jobs => {
        this.jobs = jobs;
        this.siteName = this.siteName || jobs[0]?.site?.name || '';
        this.hasLoaded = true;
      },
      error: () => {
        this.hasLoadError = true;
        this.hasLoaded = true;
      }
    });
  }

  goBack(): void {
    void this.router.navigate(['openjobs']);
  }

  openJob(job: Job): void {
    if (this.isPpm) {
      void this.closedownService.directToClosedown(false, job.companyReferenceNumber);
      return;
    }

    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType, job.id],
      { queryParams: { siteName: this.siteName } }
    );
  }

  openAllocateTo(job: Job): void {
    this.selectedJob = job;
    this.allocationMode = AllocationMode.INTERNAL;
    this.resetAllocationFields();
  }

  closeAllocateTo(): void {
    if (!this.isAllocating) {
      this.selectedJob = undefined;
      this.allocationErrorKey = '';
    }
  }

  changeAllocationMode(): void {
    this.resetAllocationFields();
  }

  confirmAllocation(): void {
    if (!this.selectedJob || !this.canConfirmAllocation || this.isAllocating) {
      return;
    }

    const selectedJob = this.selectedJob;
    const payload: PickupJob = {
      jobId: selectedJob.referenceNumber,
      resourceId: selectedJob.companyReferenceNumber,
      subContractorEmail: this.allocation.email.trim(),
      companyName: this.allocationMode === AllocationMode.INTERNAL
        ? this.getInternalCompanyName(selectedJob)
        : this.allocation.companyName.trim(),
      technicianName: this.allocation.technicianName.trim(),
      mobileNumber: this.allocation.phoneNumber.trim(),
      jobType: selectedJob.jobType
    };

    this.isAllocating = true;
    this.allocationErrorKey = '';
    this.jobService.pickupJob(payload).subscribe({
      next: result => {
        this.isAllocating = false;
        if (!result) {
          this.allocationErrorKey = 'open_jobs_list_allocate_error';
          return;
        }

        selectedJob.allocatedUserEmail = payload.subContractorEmail;
        selectedJob.allocatedReferenceId = payload.resourceId;
        this.selectedJob = undefined;
      },
      error: () => {
        this.isAllocating = false;
        this.allocationErrorKey = 'open_jobs_list_allocate_error';
      }
    });
  }

  private resetAllocationFields(): void {
    this.allocation = {
      email: '',
      companyName: '',
      technicianName: '',
      phoneNumber: ''
    };
    this.allocationErrorKey = '';
  }

  private getInternalCompanyName(job: Job): string {
    return this.currentUser?.companies?.find(company =>
      company.referenceNumber === job.companyReferenceNumber
    )?.name || job.companyReferenceNumber;
  }

  private matchesJobType(job: Job): boolean {
    if (this.isPpm) {
      return job.jobType?.toUpperCase() === JobType.PPM_JOB;
    }

    if (job.jobType?.toUpperCase() === JobType.PPM_JOB) {
      return false;
    }

    const statusesByJobType: Record<string, string[]> = {
      [OpenJobsListType.NEW]: [JobStatus.CALLOUT],
      [OpenJobsListType.ON_SITE]: [JobStatus.ONSITE],
      [OpenJobsListType.RETURNING]: [JobStatus.RETURNING],
      [OpenJobsListType.AWAITING_PARTS]: [JobStatus.AWAITING_PARTS]
    };
    const status = job.jobStatusName?.replaceAll('-', '').replaceAll(' ', '').toUpperCase();
    return statusesByJobType[this.jobType.toUpperCase()]?.some(jobStatus =>
      jobStatus.replaceAll('-', '').replaceAll(' ', '').toUpperCase() === status
    ) ?? false;
  }
}